import { useEffect, useState } from "react";
import { Gem, Bomb, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Overlay } from "@/components/Overlay";
import { GameHeader, BetControls } from "@/components/games/GameShell";
import { CoinIcon } from "@/components/Brand";
import { api, errMsg, fmt } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const GRID = 25;
const MINE_OPTS = [1, 3, 5, 10, 24];

export function MinesModal({ game, onClose }) {
  const { currency, setUser } = useApp();
  const [bet, setBet] = useState(currency === "gc" ? 100 : 0.5);
  useEffect(() => { setBet(currency === "gc" ? 100 : 0.5); }, [currency, setBet]);
  const [mines, setMines] = useState(3);
  const [active, setActive] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [busy, setBusy] = useState(false);
  const [ended, setEnded] = useState(null);

  const reset = () => { setActive(null); setRevealed({}); setEnded(null); };

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/games/mines/start", { currency, bet, mines, game_id: game.id });
      setUser(data.user);
      setActive({ id: data.game_id, mines, multiplier: 1, next: data.next_multiplier, potential: 0 });
      setRevealed({}); setEnded(null);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const pick = async (tile) => {
    if (!active || revealed[tile] || busy || ended) return;
    setBusy(true);
    try {
      const { data } = await api.post("/games/mines/reveal", { game_id: active.id, tile });
      if (data.result === "mine") {
        const r = { ...revealed }; data.mine_tiles.forEach((t) => (r[t] = "mine")); r[tile] = "boom";
        setRevealed(r); setEnded({ lost: true }); setUser(data.user);
        toast.error("Boom! You hit a mine.");
      } else if (data.result === "cashed") {
        setRevealed((p) => ({ ...p, [tile]: "gem" })); setUser(data.user);
        setEnded({ win: data.win, mult: data.multiplier }); toast.success(`Cleared! Won ${fmt(data.win, currency)} ${currency.toUpperCase()}`);
      } else {
        setRevealed((p) => ({ ...p, [tile]: "gem" }));
        setActive((a) => ({ ...a, multiplier: data.multiplier, next: data.next_multiplier, potential: data.potential }));
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const cashout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/games/mines/cashout", { game_id: active.id });
      setUser(data.user); setEnded({ win: data.win, mult: data.multiplier });
      const r = { ...revealed }; data.mine_tiles.forEach((t) => { if (!r[t]) r[t] = "mine"; }); setRevealed(r);
      toast.success(`Cashed out ${fmt(data.win, currency)} ${currency.toUpperCase()}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Overlay open={!!game} onClose={() => { reset(); onClose(); }} testId="mines-modal" className="max-w-md">
      {game && (
        <div>
          <GameHeader game={game} />
          <div className="p-5">
            {active && (
              <div className="mb-4 flex items-center justify-between rounded-xl bg-[#1F2633] px-4 py-2.5 text-sm" data-testid="mines-status">
                <span className="text-gray-400">Next: <span className="sc-text font-bold">{active.next}×</span></span>
                <span className="font-display font-black gc-text" data-testid="mines-multiplier">{active.multiplier}×</span>
                <span className="flex items-center gap-1 text-gray-400">Win <CoinIcon cur={currency} size={13} /><span className="text-white font-bold">{fmt(active.potential, currency)}</span></span>
              </div>
            )}
            <div className="grid grid-cols-5 gap-2" data-testid="mines-grid">
              {Array.from({ length: GRID }).map((_, t) => {
                const st = revealed[t];
                const open = !!st;
                return (
                  <button
                    key={t}
                    data-testid={`mines-tile-${t}`}
                    disabled={!active || ended || busy || open}
                    onClick={() => pick(t)}
                    className={`aspect-square rounded-xl flex items-center justify-center font-black transition-[transform,background-color] active:scale-95 ${
                      st === "gem" ? "bg-[#10B981]/20 ring-1 ring-[#10B981]" :
                      st === "boom" ? "bg-[#EF4444]/30 ring-1 ring-[#EF4444]" :
                      st === "mine" ? "bg-[#EF4444]/10 ring-1 ring-[#EF4444]/40" :
                      active && !ended ? "bg-[#2A3346] hover:bg-[#333f56]" : "bg-[#1A202C]"}`}
                  >
                    {st === "gem" && <Gem size={22} className="text-[#10B981]" />}
                    {(st === "boom" || st === "mine") && <Bomb size={22} className={st === "boom" ? "text-[#EF4444]" : "text-[#EF4444]/60"} />}
                  </button>
                );
              })}
            </div>

            {!active || ended ? (
              <>
                {ended && (
                  <div className="mt-4 text-center font-display text-lg font-black" data-testid="mines-result">
                    {ended.lost ? <span className="text-[#EF4444]">You hit a mine</span> : <span className="sc-text">Won {ended.mult}× — {fmt(ended.win, currency)} {currency.toUpperCase()}</span>}
                  </div>
                )}
                <div className="mt-4 flex gap-2" data-testid="mines-setup">
                  <div className="flex-1">
                    <div className="text-xs text-gray-400 mb-1.5">Mines</div>
                    <div className="flex gap-1">
                      {MINE_OPTS.map((m) => (
                        <button key={m} data-testid={`mines-count-${m}`} onClick={() => setMines(m)} className={`flex-1 rounded-lg py-2 text-sm font-bold border transition-colors ${mines === m ? "border-[#FFB800] bg-[#FFB800]/10 gc-text" : "border-white/10 text-gray-400"}`}>{m}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-4"><BetControls bet={bet} setBet={setBet} disabled={busy} /></div>
                <button data-testid="mines-start-btn" disabled={busy} onClick={start} className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl bg-[#FFB800] text-[#0B0E14] py-4 font-display text-xl font-black hover:brightness-110 active:scale-[0.98] disabled:opacity-60 transition-[filter,transform,opacity]">
                  {busy && <Loader2 className="animate-spin" size={20} />}{ended ? "PLAY AGAIN" : "START GAME"}
                </button>
              </>
            ) : (
              <button data-testid="mines-cashout-btn" disabled={busy || active.multiplier <= 1} onClick={cashout} className="mt-4 w-full rounded-xl bg-[#10B981] text-[#0B0E14] py-4 font-display text-xl font-black hover:brightness-110 active:scale-[0.98] disabled:opacity-40 transition-[filter,transform,opacity]">
                {active.multiplier <= 1 ? "PICK A TILE TO START" : `CASH OUT ${fmt(active.potential, currency)} ${currency.toUpperCase()}`}
              </button>
            )}
          </div>
        </div>
      )}
    </Overlay>
  );
}
